/**
 * 自動列印略過紀錄的 localStorage key
 */
export const PRINT_SUPPRESSION_STORAGE_KEY = 'printSuppressedMessagesV1'

/**
 * 略過紀錄必須完整保留三分鐘，不得因清理或筆數限制提早移除
 */
export const PRINT_SUPPRESSION_TTL_MS = 3 * 60 * 1000

const PRINT_MACHINE_TYPES = new Set(['pos', 'posSecond', 'tag'])
const volatileRecords = {}

const getDefaultStorage = () => {
	if (typeof window === 'undefined') {
		return null
	}
	try {
		return window.localStorage
	} catch (error) {
		return null
	}
}

export const getPrintJobKey = ({ storeId, messageId, machineType }) => {
	if (!storeId || !messageId || !PRINT_MACHINE_TYPES.has(machineType)) {
		return null
	}
	return `${storeId}:${machineType}:${messageId}`
}

const readRecords = storage => {
	if (!storage) {
		return {}
	}
	try {
		const records = JSON.parse(
			storage.getItem(PRINT_SUPPRESSION_STORAGE_KEY) || '{}',
		)
		return records && typeof records === 'object' && !Array.isArray(records)
			? records
			: {}
	} catch (error) {
		return {}
	}
}

const writeRecords = (storage, records) => {
	if (!storage) {
		return false
	}
	try {
		storage.setItem(PRINT_SUPPRESSION_STORAGE_KEY, JSON.stringify(records))
		return true
	} catch (error) {
		return false
	}
}

const pruneExpiredRecords = (records, now) => {
	let changed = false
	for (const [key, expiresAt] of Object.entries(records)) {
		if (!Number.isFinite(expiresAt) || expiresAt <= now) {
			delete records[key]
			changed = true
		}
	}
	return changed
}

/**
 * 將本機無法列印的推送保留三分鐘；期間不因筆數上限提早淘汰。
 *
 * @param {{storeId: number|string, messageId: string, machineType: string}} data 列印推送識別
 * @param {Storage|null} storage 儲存空間，測試時可注入
 * @param {number} now 現在時間，測試時可注入
 * @returns {boolean} 是否成功寫入
 */
export const suppressAutomaticPrint = (
	data,
	storage = getDefaultStorage(),
	now = Date.now(),
) => {
	const key = getPrintJobKey(data)
	if (!key) {
		return false
	}
	const records = readRecords(storage)
	pruneExpiredRecords(records, now)
	const expiresAt = now + PRINT_SUPPRESSION_TTL_MS
	volatileRecords[key] = expiresAt
	records[key] = expiresAt
	return writeRecords(storage, records)
}

/**
 * 判斷這台 APP 是否已在藍牙不可用時看過該推送。
 *
 * @param {{storeId: number|string, messageId: string, machineType: string}} data 列印推送識別
 * @param {Storage|null} storage 儲存空間，測試時可注入
 * @param {number} now 現在時間，測試時可注入
 * @returns {boolean} true 表示三分鐘內必須略過
 */
export const isAutomaticPrintSuppressed = (
	data,
	storage = getDefaultStorage(),
	now = Date.now(),
) => {
	const key = getPrintJobKey(data)
	if (!key) {
		return false
	}
	pruneExpiredRecords(volatileRecords, now)
	if (volatileRecords[key] > now) {
		return true
	}
	const records = readRecords(storage)
	const changed = pruneExpiredRecords(records, now)
	if (changed) {
		writeRecords(storage, records)
	}
	return Number.isFinite(records[key]) && records[key] > now
}
